import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { warmTransition } from '@/shared/motion/motionPresets';
import type { BulkReferralRowResult } from '../../../../types/bulk-referral.type';
import { CareSeekerBadge } from './CareSeekerBadge';
import { hintFor } from './errorHints';
import { findRowErrorValue, formatRole, hasRowError } from './reconciliationHelpers';

type InspectionPanelProps = {
  row: BulkReferralRowResult | null;
  onClose: () => void;
};

const Field = ({ column, value, flagged }: { column: string; value?: string; flagged: boolean }) => (
  <div
    data-testid={`field-${column}`}
    data-flagged={flagged}
    className={`rounded-md px-2 py-1.5 ${flagged ? 'bg-red-50 ring-1 ring-red-200' : 'bg-gray-50'}`}
  >
    <p className="text-[11px] uppercase tracking-wide text-gray-400">{column}</p>
    <p className={`truncate font-mono text-xs ${flagged ? 'text-red-800' : 'text-gray-700'}`}>{value ?? '—'}</p>
  </div>
);

/** Optional `referred_on_behalf_of` party: absent + no error = "Not provided". */
const OnBehalfOfBlock = ({ row }: { row: BulkReferralRowResult }) => {
  const person = row.onBehalfOf;
  const flagged = hasRowError(row, 'referred_on_behalf_of');
  return (
    <section>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Referred on behalf of
      </h4>
      <div className="rounded-md bg-gray-50 px-2 py-1.5">
        {person ? (
          <>
            <p className="truncate text-sm font-medium text-gray-900">{person.name}</p>
            <p className="truncate text-xs text-gray-500">
              {formatRole(person.role)}
              {person.company ? ` · ${person.company}` : ''}
            </p>
          </>
        ) : flagged ? (
          <p className="text-sm text-red-700">
            Can't be used as the on-behalf-of professional ({findRowErrorValue(row, 'referred_on_behalf_of') ?? '—'})
          </p>
        ) : (
          <p className="text-sm text-gray-400">Not provided</p>
        )}
      </div>
    </section>
  );
};

const PersonBlock = ({
  title,
  person,
  emailColumn,
  roleColumn,
  row,
}: {
  title: string;
  person: BulkReferralRowResult['referring'];
  emailColumn: 'referring_email' | 'referred_email';
  roleColumn: 'referring_role' | 'referred_role';
  row: BulkReferralRowResult;
}) => (
  <section>
    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
      <div className="space-y-1">
        <Field column={emailColumn} value={findRowErrorValue(row, emailColumn)} flagged={hasRowError(row, emailColumn)} />
        <Field column={roleColumn} value={findRowErrorValue(row, roleColumn)} flagged={hasRowError(row, roleColumn)} />
      </div>
      <ArrowRight className="h-4 w-4 text-gray-300" aria-hidden />
      <div className="rounded-md bg-gray-50 px-2 py-1.5">
        {person ? (
          <>
            <p className="truncate text-sm font-medium text-gray-900">{person.name}</p>
            <p className="truncate text-xs text-gray-500">
              {formatRole(person.role)}
              {person.company ? ` · ${person.company}` : ''}
            </p>
          </>
        ) : (
          <p className="text-sm text-red-700">No match found in bQuest</p>
        )}
      </div>
    </div>
  </section>
);

const CareSeekerBlock = ({ row }: { row: BulkReferralRowResult }) => {
  const careSeeker = row.careSeeker;
  const careSeekerColumns = [
    'care_seeker_email',
    'new_care_seeker_first_name',
    'new_care_seeker_last_name',
    'new_care_seeker_email',
    'send_invitation_email',
  ] as const;

  return (
    <section>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Care seeker</h4>
      <div className="space-y-1">
        {careSeekerColumns.map((column) => (
          <Field
            key={column}
            column={column}
            value={findRowErrorValue(row, column)}
            flagged={hasRowError(row, column)}
          />
        ))}
      </div>
      <div className="mt-2 rounded-md bg-gray-50 px-2 py-1.5">
        {careSeeker?.outcome === 'NONE' ? (
          <>
            <p className="truncate text-sm font-medium text-gray-900">No care seeker declared</p>
            <div className="mt-1">
              <CareSeekerBadge outcome={careSeeker.outcome} willSendInvite={careSeeker.willSendInvite} />
            </div>
          </>
        ) : careSeeker && careSeeker.outcome !== 'UNRESOLVED' ? (
          <>
            <p className="truncate text-sm font-medium text-gray-900">{careSeeker.name}</p>
            {careSeeker.email && <p className="truncate text-xs text-gray-500">{careSeeker.email}</p>}
            <div className="mt-1">
              <CareSeekerBadge outcome={careSeeker.outcome} willSendInvite={careSeeker.willSendInvite} />
            </div>
          </>
        ) : (
          <p className="text-sm text-red-700">No match found in bQuest</p>
        )}
      </div>
    </section>
  );
};

const ReferralBlock = ({ row }: { row: BulkReferralRowResult }) => (
  <section>
    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Referral</h4>
    <div className="space-y-1">
      <Field
        column="referral_occurred_at"
        value={findRowErrorValue(row, 'referral_occurred_at') ?? row.referralOccurredAt}
        flagged={hasRowError(row, 'referral_occurred_at')}
      />
      <Field column="notes" value={findRowErrorValue(row, 'notes')} flagged={hasRowError(row, 'notes')} />
    </div>
  </section>
);

export const InspectionPanel = ({ row, onClose }: InspectionPanelProps) => {
  const panelRef = useRef<HTMLElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!row) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [row, onClose]);

  // Move focus into the overlay on open and back to the row that triggered it on close,
  // since it's an Escape-closable, keyboard-reachable panel rather than a passive landmark.
  useEffect(() => {
    if (row) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
    } else {
      previouslyFocused.current?.focus();
    }
  }, [row]);

  return (
    <AnimatePresence>
      {row && (
        // Absolute overlay, never a flex sibling of the table — opening/closing it must
        // never resize the table's columns (spec §8.6's documented prototype failure mode).
        <motion.aside
          ref={panelRef}
          tabIndex={-1}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={warmTransition}
          role="dialog"
          aria-modal="false"
          aria-label={`Row ${row.rowNumber} details`}
          className="absolute inset-y-0 right-0 z-20 flex w-[26rem] flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-[-8px_0_24px_rgba(0,0,0,0.10)] focus:outline-none"
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Row {row.rowNumber}</h3>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close row details" className="h-8 w-8">
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>

          {row.errors.length > 0 && (
            <div role="alert" className="space-y-2 border-b bg-red-50/60 px-4 py-3">
              {row.errors.map((error) => (
                <div key={`${error.code}-${error.column ?? 'row'}`} data-error-item className="flex gap-2">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden />
                  <div>
                    <p className="text-sm text-red-800">{error.message}</p>
                    <p className="mt-0.5 text-xs text-gray-600">{hintFor(error.code)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 px-4 py-4">
            <PersonBlock
              title="Referring professional"
              person={row.referring}
              emailColumn="referring_email"
              roleColumn="referring_role"
              row={row}
            />
            <PersonBlock
              title="Referred professional"
              person={row.referred}
              emailColumn="referred_email"
              roleColumn="referred_role"
              row={row}
            />
            <OnBehalfOfBlock row={row} />
            <CareSeekerBlock row={row} />
            <ReferralBlock row={row} />
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
